import { ORACLE_PRICES_KEY } from '@/src/constants';
import { useLocalStorage } from '@/src/hooks';
import { Button, Flex } from '@/src/ui-components';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlusCircleIcon, SaveIcon } from 'lucide-react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useMediaQuery } from 'usehooks-ts';
import { additionalPricePairOracle, defaultPricePairOracles } from '../../constants';
import {
  type PricePairOracles,
  type PricePairOraclesForm,
  pricePairOraclesFormSchema,
} from '../../oracles.types';
import { PricePairOracleCard } from './price-pair-oracle-card';

export const PricePairOraclesList = () => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { value, update } = useLocalStorage<PricePairOracles>(
    ORACLE_PRICES_KEY,
    defaultPricePairOracles
  );

  const form = useForm<PricePairOraclesForm>({
    resolver: zodResolver(pricePairOraclesFormSchema),
    values: { pricePairOracles: value ?? defaultPricePairOracles },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'pricePairOracles',
  });

  const onSubmit: SubmitHandler<PricePairOraclesForm> = (data) => {
    update(data.pricePairOracles);
    toast.success('Price pair oracles saved successfully');
  };

  const handleAddPricePairOracle = () => {
    append(additionalPricePairOracle);
  };

  const buttons = (
    <Flex align="center" gap={4} className="my-4">
      <Button variant="outline" type="button" onClick={handleAddPricePairOracle}>
        <PlusCircleIcon /> Add Price Pair Oracle
      </Button>
      <Button type="submit">
        <SaveIcon /> Save
      </Button>
    </Flex>
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {buttons}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {fields.map((field, index) => (
            <PricePairOracleCard
              key={field.id}
              index={index}
              oraclePriceDetails={field}
              removeCard={remove}
            />
          ))}
        </div>

        {isMobile && buttons}
      </form>
    </FormProvider>
  );
};
